import firebase from 'firebase';
import React, { useEffect, useMemo, useState } from 'react';
import { LoadingSpinner } from 'components/loading-spinner/LoadingSpinner';

type AuthProviderProps = {
  firebaseConfig: {
    apiKey: string;
    authDomain: string;
    databaseURL: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
  };
};

export type AuthContextType = {
  currentUser: firebase.User | null;
  firebaseApp: firebase.app.App | null;
};

export const AuthContext = React.createContext<AuthContextType>({
  currentUser: null,
  firebaseApp: null,
});

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, firebaseConfig }) => {
  const firebaseApp = useMemo(() => {
    if (!firebase.apps.length) {
      return firebase.initializeApp(firebaseConfig);
    }

    return firebase.app();
  }, [firebaseConfig]);

  const [currentUser, setCurrentUser] = useState(firebaseApp.auth().currentUser);
  const [userLoading, setUserLoading] = useState(true);
  const AuthContextValueWrapper = useMemo(
    () => ({ currentUser, firebaseApp }),
    [currentUser, firebaseApp],
  );

  useEffect(() => {
    const unsubscribe = firebaseApp.auth().onAuthStateChanged((user) => {
      setCurrentUser(user);
      setUserLoading(false);
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (userLoading) {
    return <LoadingSpinner />;
  }

  return <AuthContext.Provider value={AuthContextValueWrapper}>{children}</AuthContext.Provider>;
};
